import React, { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react'
import { setToken, apiBaseUrl } from '@/lib/api'
import { MortiseMark } from '@/components/atoms/MortiseMark'
import { Input } from '@/components/atoms/Input'
import { Select } from '@/components/atoms/Select'

const SIZE_BANDS = ['1', '2-5', '6-15', '16-50', '50+'] as const
const TRADE_OPTIONS = [
  { value: 'General contractor', label: 'General contractor' },
  { value: 'Remodeller', label: 'Remodeller' },
  { value: 'Home builder', label: 'Home builder' },
  { value: 'Subcontractor', label: 'Subcontractor' },
  { value: 'Other', label: 'Other' },
]
const ACCOUNTING_OPTIONS = [
  { value: 'QuickBooks Online', label: 'QuickBooks Online' },
  { value: 'Sage', label: 'Sage' },
  { value: 'Xero', label: 'Xero' },
  { value: 'Other', label: 'Other' },
  { value: 'None', label: 'None' },
]

export default function MobileSignupPage() {
  useEffect(() => {
    document.title = 'Tenon — Sign Up'
    return () => { document.title = 'Tenon' }
  }, [])

  const [searchParams] = useSearchParams()
  const [name, setName] = useState(() => searchParams.get('name') || '')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState(() => searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [companySizeBand, setCompanySizeBand] = useState('')
  const [primaryTrade, setPrimaryTrade] = useState('')
  const [accountingSoftware, setAccountingSoftware] = useState('')
  const [showOptional, setShowOptional] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (!termsAccepted) {
      setError('You must agree to the Terms of Service and Privacy Policy')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`${apiBaseUrl}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          name,
          email,
          password,
          company_size_band: companySizeBand,
          primary_trade: primaryTrade,
          accounting_software: accountingSoftware,
          signup_source: searchParams.get('source') ?? 'ios-native',
          utm_source: searchParams.get('utm_source') ?? '',
          utm_medium: searchParams.get('utm_medium') ?? '',
          utm_campaign: searchParams.get('utm_campaign') ?? '',
          terms_accepted: termsAccepted,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Signup failed')
      await setToken(data.token)
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]))
        if (payload.exp) localStorage.setItem('ml_token_exp', String(payload.exp))
      } catch (_) {}
      navigate('/m/today', { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Signup failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg-page)',
      fontFamily: "'Karla', -apple-system, sans-serif",
      padding: '48px 24px 40px',
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
        <MortiseMark size={44} light />
      </div>

      <div style={{ flex: 1, maxWidth: 460, width: '100%', margin: '0 auto' }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: '0 0 8px',
          letterSpacing: '-0.025em',
          lineHeight: 1.15,
        }}>
          Welcome to Tenon
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 28, marginTop: 0, lineHeight: 1.5 }}>
          Set up your account and start building.
        </p>

        {error && (
          <div style={{
            padding: '12px 14px',
            background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
            border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
            borderRadius: 8,
            color: 'var(--st-error)',
            fontSize: 13,
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Your name</label>
            <Input
              type="text"
              placeholder="Jane Smith"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
              style={{ minHeight: 48, fontSize: 16 }}
            />
          </div>

          {/* Company name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Company name</label>
            <Input
              type="text"
              placeholder="Acme Construction"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              required
              style={{ minHeight: 48, fontSize: 16 }}
            />
          </div>

          {/* Email */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Work email</label>
            <Input
              type="email"
              placeholder="jane@acmeconstruction.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{ minHeight: 48, fontSize: 16 }}
            />
          </div>

          {/* Password with show/hide */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Min. 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{ minHeight: 48, fontSize: 16, paddingRight: 50 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 44,
                  minHeight: 44,
                  justifyContent: 'center',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Optional company info expander */}
          <button
            type="button"
            onClick={() => setShowOptional(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '0 14px',
              cursor: 'pointer',
              fontFamily: "'Karla', -apple-system, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              minHeight: 48,
            }}
          >
            <span>Tell us about your company</span>
            {showOptional ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showOptional && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingLeft: 2 }}>
              {/* Company size band */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Team size</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {SIZE_BANDS.map(band => (
                    <button
                      key={band}
                      type="button"
                      onClick={() => setCompanySizeBand(band)}
                      style={{
                        padding: '10px 16px',
                        minHeight: 44,
                        borderRadius: 7,
                        border: `1px solid ${companySizeBand === band ? 'var(--brand-gold)' : 'var(--border)'}`,
                        background: companySizeBand === band
                          ? 'color-mix(in oklch, var(--brand-gold) 12%, var(--bg-elevated))'
                          : 'var(--bg-elevated)',
                        color: companySizeBand === band ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontFamily: "'Karla', -apple-system, sans-serif",
                        fontSize: 13,
                        fontWeight: companySizeBand === band ? 700 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {band}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>What do you build?</label>
                <Select
                  options={TRADE_OPTIONS}
                  placeholder="What do you build?"
                  value={primaryTrade}
                  onChange={e => setPrimaryTrade(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Accounting software</label>
                <Select
                  options={ACCOUNTING_OPTIONS}
                  placeholder="Current accounting software"
                  value={accountingSoftware}
                  onChange={e => setAccountingSoftware(e.target.value)}
                />
              </div>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, paddingTop: 4 }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
              required
              style={{ marginTop: 3, minWidth: 18, minHeight: 18, accentColor: 'var(--brand-gold)', flexShrink: 0 }}
            />
            <span>
              I agree to the{' '}
              <Link to="/terms" target="_blank" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Privacy Policy</Link>.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              minHeight: 52,
              background: loading
                ? 'color-mix(in oklch, var(--brand-gold) 60%, transparent)'
                : 'var(--brand-gold)',
              color: 'var(--text-primary)',
              fontFamily: "'Karla', -apple-system, sans-serif",
              fontSize: 16,
              fontWeight: 700,
              border: 'none',
              borderRadius: 10,
              cursor: loading ? 'not-allowed' : 'pointer',
              letterSpacing: '-0.01em',
              marginTop: 8,
            }}
          >
            {loading ? 'Creating account…' : 'Create my account'}
          </button>

          <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)', paddingTop: 4 }}>
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: 'var(--text-primary)',
                fontWeight: 700,
                textDecoration: 'none',
                borderBottom: '2px solid var(--brand-gold)',
                paddingBottom: 1,
              }}
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>

      <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center', gap: 20 }}>
        <Link to="/terms" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>Terms</Link>
        <Link to="/privacy" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>Privacy</Link>
        <a href="/dpa" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>DPA</a>
      </div>
    </div>
  )
}
